import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { 
  navigateToTab as navigateToTabAction, 
  setNavigating 
} from '@/store/slices/navigationSlice';
import { TabNavigationOptions } from '@/types/navigation';

/**
 * Custom hook for handling tab navigation in the mobile-first bottom navigation
 * Provides methods to navigate between tabs with proper state management
 */
export function useTabNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  
  const { activeTab, isNavigating, tabHistory } = useAppSelector(
    state => state.navigation
  );

  // Map paths to tab IDs
  const pathToTabId = useCallback((path: string): string => {
    if (path === '/' || path.startsWith('/discover')) return 'discover';
    if (path.startsWith('/conversations')) return 'conversations';
    if (path.startsWith('/settings')) return 'settings';
    return 'discover'; // fallback
  }, []);

  // Map tab IDs to paths
  const tabIdToPath = useCallback((tabId: string): string => {
    switch (tabId) {
      case 'discover': return '/';
      case 'conversations': return '/conversations';
      case 'settings': return '/settings';
      default: return '/';
    }
  }, []);

  // Navigate to a specific tab
  const navigateToTab = useCallback((
    tabId: string, 
    options?: TabNavigationOptions
  ) => {
    const targetPath = tabIdToPath(tabId);
    const currentTabId = pathToTabId(location.pathname);
    
    // Don't navigate if already on the target tab
    if (currentTabId === tabId && options?.direction !== 'replace') {
      return;
    }

    // Dispatch navigation action to update state
    dispatch(navigateToTabAction({ tabId, options }));
    
    // Navigate using React Router
    if (options?.direction === 'replace') {
      navigate(targetPath, { replace: true });
    } else {
      navigate(targetPath);
    }

    // Reset navigation state after a brief delay
    setTimeout(() => {
      dispatch(setNavigating(false));
    }, 300);
  }, [dispatch, navigate, location.pathname, pathToTabId, tabIdToPath]);

  // Go back to previous tab
  const goBack = useCallback(() => {
    if (tabHistory.length > 1) {
      const previousTabId = tabHistory[tabHistory.length - 2];
      navigateToTab(previousTabId, { 
        direction: 'backward',
        preventHistory: true 
      });
    }
  }, [tabHistory, navigateToTab]);

  // Check if we can go back
  const canGoBack = tabHistory.length > 1;

  // Get current tab ID from the current path
  const getCurrentTabId = useCallback(() => {
    return pathToTabId(location.pathname);
  }, [location.pathname, pathToTabId]);

  return {
    // State
    activeTab,
    isNavigating,
    canGoBack,
    tabHistory,
    
    // Methods
    navigateToTab,
    goBack,
    getCurrentTabId,
    
    // Utilities
    pathToTabId,
    tabIdToPath,
  };
}

/**
 * Main API function for handling tab navigation
 * This is used by components that need to trigger navigation programmatically
 */
export function handleTabNavigation(
  tabId: string,
  options?: TabNavigationOptions
) {
  // This function is meant to be used with the hook above
  // The actual implementation is in the useTabNavigation hook
  console.warn(
    'handleTabNavigation should be used via useTabNavigation hook for proper React context'
  );
  
  return {
    success: false,
    error: 'Use useTabNavigation hook instead',
  };
}

export default useTabNavigation;